/**
 * ngPrometZoneModel — the pure half of the zone view »Promet po conah« (#711):
 * the form's state, its defaults and validation, the query the server gets,
 * and what the map overlay shows. No DOM, no fetch, no map.
 *
 * The figures themselves are the server's (#709); nothing here computes one.
 */

import type {
  PrometAreaKind,
  PrometZoneInterval,
  PrometZonesResponse,
} from '../../../../../../shared/types/ngPromet.js';
import {
  ALL_WEEKDAYS,
  DEFAULT_PROMET_CATEGORIES,
  appliedFilters,
  categoriesParam,
  prometUnit,
  weekdaysParam,
  type PrometCategory,
} from '../../../../../../shared/utils/ngPrometFilters.js';
import { publicPeriodProblem, type PublicPeriodLimits } from '../../../../../../shared/utils/ngPrometPublic.js';
import {
  NO_DATA_LABEL,
  areaNetLabel,
  directionLetters,
  pointShareLabel,
} from './ngPrometZoneFormat.js';

// ----------------------------------------------------------------------------
// Days — plain 'YYYY-MM-DD' strings (Europe/Ljubljana calendar days), never a
// JS Date in the state: no timezone can then move a day.
// ----------------------------------------------------------------------------

/** Today's date in Ljubljana, 'YYYY-MM-DD'. */
export function ljubljanaToday(now: Date = new Date()): string {
  // sv-SE prints ISO order.
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Ljubljana',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n)).toISOString().slice(0, 10);
}

/** The Ljubljana day and hour of day an instant falls in. */
export function ljubljanaHourOf(iso: string): { day: string; hour: number } | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Ljubljana',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const hour = Number(get('hour'));
  if (!Number.isFinite(hour)) return null;
  return { day: `${get('year')}-${get('month')}-${get('day')}`, hour };
}

/** True for the first of two instants that share one Ljubljana day and hour (autumn clock change). */
export function isFirstOfRepeatedHour(iso: string): boolean {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return false;
  const here = ljubljanaHourOf(iso);
  const next = ljubljanaHourOf(new Date(at + 3_600_000).toISOString());
  return here !== null && next !== null && here.day === next.day && here.hour === next.hour;
}

export interface DayRange {
  from: string;
  to: string;
}

/** How many days the default reference period R covers. */
export const DEFAULT_REFERENCE_DAYS = 30;

/** The default R: the 30 days that end yesterday. */
export function defaultReference(today: string): DayRange {
  return { from: addDays(today, -DEFAULT_REFERENCE_DAYS), to: addDays(today, -1) };
}

/**
 * When the data ends before the default R starts (a frozen copy, a long
 * outage), R moves to the 30 days ending on the last day that has data.
 * `null` = the current R already reaches the data, keep it.
 */
export function reanchoredReference(lastDay: string | null, current: DayRange): DayRange | null {
  if (!lastDay || lastDay >= current.from) return null;
  return { from: addDays(lastDay, -(DEFAULT_REFERENCE_DAYS - 1)), to: lastDay };
}

// ----------------------------------------------------------------------------
// Form state, validation, query
// ----------------------------------------------------------------------------

/** The periods as typed. An empty P means »the last available day / hour«. */
export interface ZonePeriods {
  interval: PrometZoneInterval;
  rFrom: string | null;
  rTo: string | null;
  /** Day interval: the first day of P. Hour interval: the day of the hour. */
  pFrom: string | null;
  /** Day interval only: the last day of P (empty = one day). */
  pTo: string | null;
  /** Hour interval only: the hour of the day, 0–23. */
  pHour: number | null;
}

export type ZoneField = 'rFrom' | 'rTo' | 'pFrom' | 'pTo' | 'pHour';
export type ZoneFormProblems = Partial<Record<ZoneField, string>>;

/**
 * What is wrong with the periods, per field; `{}` = the form may be sent.
 *
 * `limits` = the public page's caps (#867): R, a day-P, and the window from the
 * first day of the earlier period to the last day of the later one. An empty P
 * is the last available day — `lastDay`, from the answer on screen; unknown,
 * the server checks the window alone.
 */
export function validateZonePeriods(
  s: ZonePeriods,
  limits?: PublicPeriodLimits,
  lastDay?: string | null,
): ZoneFormProblems {
  const out: ZoneFormProblems = {};
  if (!s.rFrom) out.rFrom = 'Vnesite začetek referenčnega obdobja.';
  if (!s.rTo) out.rTo = 'Vnesite konec referenčnega obdobja.';
  if (s.rFrom && s.rTo && s.rTo < s.rFrom) out.rTo = 'Konec obdobja je pred začetkom.';

  if (s.interval === 'hour') {
    if (s.pFrom && s.pHour === null) out.pHour = 'Izberite uro.';
    if (!s.pFrom && s.pHour !== null) out.pFrom = 'Vnesite dan.';
  } else {
    if (!s.pFrom && s.pTo) out.pFrom = 'Vnesite začetek obdobja.';
    if (s.pFrom && s.pTo && s.pTo < s.pFrom) out.pTo = 'Konec obdobja je pred začetkom.';
  }

  // The caps judge only periods that are otherwise valid.
  if (limits && s.rFrom && s.rTo && Object.keys(out).length === 0) {
    const pFrom = s.pFrom ?? lastDay ?? null;
    const pTo = s.pFrom ? (s.interval === 'day' ? s.pTo ?? s.pFrom : s.pFrom) : (lastDay ?? null);
    const problem = publicPeriodProblem(
      { referenceFrom: s.rFrom, referenceTo: s.rTo, comparisonFrom: pFrom, comparisonTo: pTo },
      limits,
    );
    if (problem?.kind === 'reference') {
      out.rTo = `Referenčno obdobje je lahko dolgo največ ${limits.referenceDays} dni.`;
    } else if (problem?.kind === 'comparison') {
      out.pTo = `Obdobje za primerjavo je lahko dolgo največ ${limits.comparisonDays} dni.`;
    } else if (problem?.kind === 'window') {
      out[s.pFrom ? 'pFrom' : 'rFrom'] =
        `Od začetka prvega do konca zadnjega obdobja je lahko največ ${limits.windowDays} dni.`;
    }
  }
  return out;
}

/**
 * The two filters of the form (#740): the traffic classes that are summed and
 * the weekdays (ISO, 1 = Monday) that R and a P of several days average.
 */
export interface ZoneFilters {
  categories: PrometCategory[];
  weekdays: number[];
}

/** The form's start: today's motor total on every day — the same as sending no filter. */
export function defaultZoneFilters(): ZoneFilters {
  return { categories: [...DEFAULT_PROMET_CATEGORIES], weekdays: [...ALL_WEEKDAYS] };
}

export type ZoneFilterField = keyof ZoneFilters;

/** What is wrong with the filters, per group; `{}` = the form may be sent. An empty group is never a request. */
export function validateZoneFilters(f: ZoneFilters): Partial<Record<ZoneFilterField, string>> {
  const out: Partial<Record<ZoneFilterField, string>> = {};
  if (f.categories.length === 0) out.categories = 'Izberite vsaj eno kategorijo.';
  if (f.weekdays.length === 0) out.weekdays = 'Izberite vsaj en dan.';
  return out;
}

/** One request to `/promet/zones`: validated periods + the two selections. */
export interface ZoneQuery {
  interval: PrometZoneInterval;
  rFrom: string;
  rTo: string;
  pFrom: string | null;
  pTo: string | null;
  pHour: number | null;
  /** Area key from the response (`zone:<id>` / `line:<id>`); null = the server's first. */
  area: string | null;
  pointId: number | null;
  /** The summed traffic classes (#740); absent = the default (car, heavy, night). */
  categories?: PrometCategory[];
  /** The ISO weekdays that are averaged (#740); absent = every day. */
  weekdays?: number[];
}

export function buildZonesQuery(q: ZoneQuery): string {
  const p = new URLSearchParams();
  p.set('interval', q.interval);
  p.set('reference_from', q.rFrom);
  p.set('reference_to', q.rTo);
  // An empty P is left out: the server then takes the last available day / hour.
  if (q.pFrom) {
    p.set('comparison_from', q.pFrom);
    if (q.interval === 'hour') {
      if (q.pHour !== null) p.set('comparison_hour', String(q.pHour));
    } else if (q.pTo && q.pTo !== q.pFrom) {
      p.set('comparison_to', q.pTo);
    }
  }
  if (q.area) p.set('area', q.area);
  if (q.pointId !== null) p.set('access_point_id', String(q.pointId));
  // A filter is sent only when it differs from the default, so a request
  // without one — and every older link — stays what it was (#740).
  const categories = q.categories ? categoriesParam(q.categories) : null;
  if (categories) p.set('categories', categories);
  const weekdays = q.weekdays ? weekdaysParam(q.weekdays) : null;
  if (weekdays) p.set('weekdays', weekdays);
  return p.toString();
}

/**
 * The query of an answer, pinned to what the server actually used: its area,
 * its point, and — when P was left empty — the comparison period it resolved.
 * An area or point change, an export and the report in a new tab reuse THIS
 * query, so they keep the periods on screen even if newer data arrived in the
 * meantime. Only »Prikaži« with an empty P asks for »the last available« again.
 *
 * One hour a year cannot be pinned: the FIRST of the two 02:00–03:00 hours on
 * the night the clocks go back. Day + hour name the second one on the server,
 * so for that instant P stays as it was asked (empty = the last available).
 */
export function pinnedQuery(query: ZoneQuery, response: PrometZonesResponse): ZoneQuery {
  const used = response.comparison;
  const pinnable = used !== null && !(query.interval === 'hour' && isFirstOfRepeatedHour(used.from));
  return {
    ...query,
    area: response.area,
    pointId: response.access_point_id,
    ...(used && pinnable
      ? {
          pFrom: used.first_day,
          pTo: query.interval === 'day' ? used.last_day : null,
          pHour: query.interval === 'hour' ? used.hour : null,
        }
      : {}),
  };
}

// ----------------------------------------------------------------------------
// Map overlay — what the map shows for the zone view
// ----------------------------------------------------------------------------

/**
 * The Priloga's colours (§3.2.1): the whole area a red line, the north light
 * blue, the south dark blue, the transition yellow. They go by the area's
 * PLACE in the response (zones come by id, then the line) — no id or slug
 * table in code.
 */
const ZONE_COLORS = ['#dc2626', '#38bdf8', '#1e3a8a'];
const LINE_COLOR = '#eab308';
const SPARE_COLOR = '#64748b';

/** Area key → its colour, for the radios' swatches and the map. */
export function areaColors(areas: { key: string; kind: PrometAreaKind }[]): Map<string, string> {
  const out = new Map<string, string>();
  let zone = 0;
  for (const a of areas) {
    out.set(a.key, a.kind === 'line' ? LINE_COLOR : (ZONE_COLORS[zone++] ?? SPARE_COLOR));
  }
  return out;
}

export interface ZoneOverlayArea {
  key: string;
  kind: PrometAreaKind;
  id: number;
  name: string;
  color: string;
  /** The area the figures are for: bold, zones with a light fill. */
  chosen: boolean;
  /** The area under the mouse in the panel: a medium emphasis and its name. */
  preview: boolean;
}

export interface ZoneOverlayPoint {
  id: number;
  name: string;
  /** Two lines (»V: P 14 % | R 10 %« / »I: …«) or »brez podatkov«. */
  label: string;
  counted: boolean;
  selected: boolean;
}

export interface ZoneOverlayState {
  /** Every area of the response; one is chosen, at most one previewed. */
  areas: ZoneOverlayArea[];
  /** »P: +220 voz.« / »R: +200 voz.« of the chosen area. */
  areaLabel: string;
  /** The chosen area's access points, in its colour. */
  points: ZoneOverlayPoint[];
  pointColor: string;
  /** Access points of the previewed area — all of them: the chosen area's own step back meanwhile. */
  previewPointIds: number[];
}

/**
 * What the map shows: all areas (the chosen one flagged), the chosen area's
 * points with their labels, and — while the mouse is over another area's radio
 * — that area's preview, with the chosen area stepped back (`overlayFeatures`).
 * `null` when the answer does not hold the chosen area.
 */
export function buildOverlayState(
  response: PrometZonesResponse,
  areaKey: string,
  pointId: number | null,
  previewKey: string | null = null,
): ZoneOverlayState | null {
  const area = response.areas.find((a) => a.key === areaKey);
  if (!area) return null;
  const colors = areaColors(response.areas);
  const letters = directionLetters(area.kind);
  const preview = previewKey !== areaKey ? response.areas.find((a) => a.key === previewKey) : undefined;
  // Vehicles only while the answer counts motor classes alone (#740).
  const unit = prometUnit(appliedFilters(response.applied).categories);
  return {
    areas: response.areas.map((a) => ({
      key: a.key,
      kind: a.kind,
      id: a.id,
      name: a.name,
      color: colors.get(a.key) ?? SPARE_COLOR,
      chosen: a.key === area.key,
      preview: a.key === preview?.key,
    })),
    areaLabel:
      area.p && area.r
        ? `${areaNetLabel('P', area.p.net, unit)}\n${areaNetLabel('R', area.r.net, unit)}`
        : NO_DATA_LABEL,
    points: area.points.map((pt) => ({
      id: pt.access_point_id,
      name: pt.name,
      label:
        pt.counted && pt.p && pt.r
          ? `${pointShareLabel(letters.in, pt.p.in_share_pct, pt.r.in_share_pct)}\n` +
            pointShareLabel(letters.out, pt.p.out_share_pct, pt.r.out_share_pct)
          : NO_DATA_LABEL,
      counted: pt.counted,
      selected: pt.access_point_id === pointId,
    })),
    pointColor: colors.get(area.key) ?? SPARE_COLOR,
    previewPointIds: (preview?.points ?? []).map((p) => p.access_point_id),
  };
}

/** The geometry the overlay fetched once: by microzone / line / access point id. */
export interface ZoneGeometry {
  zones: Map<number, GeoJSON.Geometry>;
  lines: Map<number, GeoJSON.Geometry>;
  points: Map<number, [number, number]>;
}

type Ring = GeoJSON.Position[];

function ringCentroid(ring: Ring): { c: [number, number]; area: number } | null {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x0 = 0, y0 = 0] = ring[i] ?? [];
    const [x1 = 0, y1 = 0] = ring[i + 1] ?? [];
    const f = x0 * y1 - x1 * y0;
    a += f;
    cx += (x0 + x1) * f;
    cy += (y0 + y1) * f;
  }
  if (a === 0) return null;
  return { c: [cx / (3 * a), cy / (3 * a)], area: Math.abs(a / 2) };
}

function lineMidpoint(line: GeoJSON.Position[]): { c: [number, number]; length: number } | null {
  const seg: number[] = [];
  let total = 0;
  for (let i = 0; i < line.length - 1; i++) {
    const [x0 = 0, y0 = 0] = line[i] ?? [];
    const [x1 = 0, y1 = 0] = line[i + 1] ?? [];
    const len = Math.hypot(x1 - x0, y1 - y0);
    seg.push(len);
    total += len;
  }
  if (total === 0) return null;
  let rest = total / 2;
  for (let i = 0; i < seg.length; i++) {
    const len = seg[i] ?? 0;
    if (rest <= len && len > 0) {
      const [x0 = 0, y0 = 0] = line[i] ?? [];
      const [x1 = 0, y1 = 0] = line[i + 1] ?? [];
      const t = rest / len;
      return { c: [x0 + (x1 - x0) * t, y0 + (y1 - y0) * t], length: total };
    }
    rest -= len;
  }
  return null;
}

/**
 * Where an area's own label goes: the centroid of its largest polygon, or the
 * middle of its longest line. One point — a symbol placed on the polygon
 * itself would repeat in every tile the polygon touches.
 */
export function labelPoint(geometry: GeoJSON.Geometry): [number, number] | null {
  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    let best: { c: [number, number]; area: number } | null = null;
    for (const poly of polygons) {
      const outer = poly[0];
      const c = outer ? ringCentroid(outer) : null;
      if (c && (!best || c.area > best.area)) best = c;
    }
    return best?.c ?? null;
  }
  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    const lines = geometry.type === 'LineString' ? [geometry.coordinates] : geometry.coordinates;
    let best: { c: [number, number]; length: number } | null = null;
    for (const line of lines) {
      const m = lineMidpoint(line);
      if (m && (!best || m.length > best.length)) best = m;
    }
    return best?.c ?? null;
  }
  return null;
}

/**
 * The overlay's whole content as one FeatureCollection. `role` tells the
 * layers apart: 'area' (a zone polygon or the line), 'area_label' (the chosen
 * area's figures, one point), 'area_name' (the previewed area's name),
 * 'point' (an access point of the chosen area), 'preview_point'.
 *
 * While an area is previewed (#863), the chosen one steps back so the hovered
 * one reads first: its area feature is `dimmed` (no fill, a plain line), its
 * figure label and its points are left out, and the previewed area paints on
 * top. `chosen` keeps its meaning on every feature.
 */
export function overlayFeatures(state: ZoneOverlayState, geometry: ZoneGeometry): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  const geometryOf = (a: ZoneOverlayArea): GeoJSON.Geometry | undefined =>
    a.kind === 'zone' ? geometry.zones.get(a.id) : geometry.lines.get(a.id);
  const previewing = state.areas.some((a) => a.preview);

  // Features of one layer paint in this order: the plain areas first, then the
  // chosen one on top — or, during a preview, the previewed one on top.
  const rank = (a: ZoneOverlayArea): number => ((previewing ? a.preview : a.chosen) ? 1 : 0);
  for (const area of [...state.areas].sort((a, b) => rank(a) - rank(b))) {
    const g = geometryOf(area);
    if (!g) continue;
    const dimmed = area.chosen && previewing;
    features.push({
      type: 'Feature',
      geometry: g,
      properties: { role: 'area', kind: area.kind, color: area.color, chosen: area.chosen, preview: area.preview, dimmed },
    });
    const text = dimmed ? '' : area.chosen ? state.areaLabel : area.preview ? area.name : '';
    const at = text ? labelPoint(g) : null;
    if (at) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: at },
        properties: { role: area.chosen ? 'area_label' : 'area_name', label: text },
      });
    }
  }

  for (const pt of previewing ? [] : state.points) {
    const at = geometry.points.get(pt.id);
    if (!at) continue;
    features.push({
      type: 'Feature',
      id: pt.id,
      geometry: { type: 'Point', coordinates: at },
      properties: {
        role: 'point',
        id: pt.id,
        name: pt.name,
        label: pt.label,
        color: state.pointColor,
        counted: pt.counted,
        selected: pt.selected,
      },
    });
  }

  const previewColor = state.areas.find((a) => a.preview)?.color ?? SPARE_COLOR;
  for (const id of state.previewPointIds) {
    const at = geometry.points.get(id);
    if (!at) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: at },
      properties: { role: 'preview_point', color: previewColor },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** The part of the overlay »Pokaži na zemljevidu« fits the map to: the chosen area and its points. */
export function chosenFeatures(fc: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: fc.features.filter((f) => {
      const props = f.properties as { role?: unknown; chosen?: unknown } | null;
      return props?.role === 'point' || (props?.role === 'area' && props.chosen === true);
    }),
  };
}

/** [west, south, east, north] of a feature collection; null when empty. */
export function featureBounds(fc: GeoJSON.FeatureCollection): [number, number, number, number] | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      w = Math.min(w, c[0]);
      e = Math.max(e, c[0]);
      s = Math.min(s, c[1]);
      n = Math.max(n, c[1]);
      return;
    }
    for (const inner of c) visit(inner);
  };
  for (const f of fc.features) {
    if (f.geometry && 'coordinates' in f.geometry) visit(f.geometry.coordinates);
  }
  return Number.isFinite(w) ? [w, s, e, n] : null;
}
